/**
 * Index / landing page for the Union Payroll module.
 *
 * Card-based overview linking to each sub-page.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Stack,
  Typography,
} from "@mui/material";
import GroupsIcon from "@mui/icons-material/Groups";
import GavelIcon from "@mui/icons-material/Gavel";
import AssignmentLateIcon from "@mui/icons-material/AssignmentLate";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import EngineeringIcon from "@mui/icons-material/Engineering";
import DescriptionIcon from "@mui/icons-material/Description";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import RuleIcon from "@mui/icons-material/Rule";
import TuneIcon from "@mui/icons-material/Tune";
import BadgeIcon from "@mui/icons-material/Badge";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";

import { api } from "@/lib/api";
import { HelpHint } from "@/components/HelpHint";

export const Route = createFileRoute("/_auth/payroll-l3/")({
  component: PayrollL3IndexPage,
});

const CARDS = [
  {
    to: "/payroll-l3/cbas",
    title: "CBAs",
    desc: "Set up unions, locals, and collective bargaining agreements with classifications, wage rates, and fringes.",
    icon: GavelIcon,
  },
  {
    to: "/payroll-l3/classification-queue",
    title: "Classification Queue",
    desc: "Resolve labor events that need a manual classification assignment.",
    icon: AssignmentLateIcon,
  },
  {
    to: "/payroll-l3/fringe-funds",
    title: "Fringe Funds",
    desc: "Configure benefit funds (H&W, pension, training, vacation) and remittance contacts.",
    icon: AccountBalanceIcon,
  },
  {
    to: "/payroll-l3/remittance",
    title: "Remittance",
    desc: "Generate and submit fringe-fund remittance batches per period.",
    icon: ReceiptLongIcon,
  },
  {
    to: "/payroll-l3/apprentice-compliance",
    title: "Apprentice Compliance",
    desc: "Monitor JW-to-apprentice ratios per project and local.",
    icon: EngineeringIcon,
  },
  {
    to: "/payroll-l3/contracts",
    title: "Contracts",
    desc: "Upload signed CBA documents for archival and AI-assisted parsing.",
    icon: DescriptionIcon,
  },
  {
    to: "/payroll-l3/working-rules",
    title: "Working Rules",
    desc: "Holiday lists, show-up pay, apprentice ratios, and other CBA operational rules read by the pay-run engine.",
    icon: RuleIcon,
  },
  {
    to: "/payroll-l3/gl-setup",
    title: "GL Setup",
    desc: "Verify the active bucket rule set + ledger template are at v3+ and the union-fringe / dues GL accounts are in your chart of accounts.",
    icon: TuneIcon,
  },
  {
    to: "/payroll-l3/memberships",
    title: "Employee Memberships",
    desc: "View and manage which union locals each employee belongs to, card numbers, dues authorization, and apprentice period tracking.",
    icon: BadgeIcon,
  },
  {
    to: "/payroll-l3/compliance-alerts",
    title: "Compliance Alerts",
    desc: "Review alerts raised during pay-run processing AND by the nightly AI audit (CBA expiry, misclassification, retro rates, more).",
    icon: NotificationsActiveIcon,
  },
] as const;

interface GLSetupBanner {
  ok: boolean;
  accounts: { missing_count: number };
  bucket_rule_set: { version_ok: boolean };
  ledger_template: { version_ok: boolean };
}

function PayrollL3IndexPage() {
  const setupQ = useQuery({
    queryKey: ["payroll-l3", "gl-setup-status", "banner"],
    queryFn: () => api<GLSetupBanner>("/api/payroll-l3/gl-setup-status"),
    staleTime: 60_000,
  });

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
        <Stack direction="row" alignItems="center" gap={1}>
          <GroupsIcon color="primary" />
          <Typography variant="h5" fontWeight={600}>
            Union Payroll
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" gap={1}>
          {setupQ.data && (
            <Chip
              icon={
                setupQ.data.ok ? (
                  <CheckCircleIcon fontSize="small" />
                ) : (
                  <ErrorIcon fontSize="small" />
                )
              }
              label={
                setupQ.data.ok
                  ? "GL setup OK"
                  : `GL setup needs attention${
                      setupQ.data.accounts.missing_count
                        ? ` (${setupQ.data.accounts.missing_count} accounts missing)`
                        : ""
                    }`
              }
              color={setupQ.data.ok ? "success" : "warning"}
              size="small"
              component={Link as any}
              to={"/payroll-l3/gl-setup" as any}
              clickable
            />
          )}
          <HelpHint docPath="payroll-l3/README.md" label="Union payroll overview" />
        </Stack>
      </Stack>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Multi-local union payroll with CBA-driven wages, fringes, dues check-off,
        remittance, and apprentice ratio enforcement.
      </Typography>

      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            md: "repeat(3, 1fr)",
          },
        }}
      >
        {CARDS.map((c) => {
          const Icon = c.icon;
          return (
            <Card key={c.to} variant="outlined">
              <CardActionArea
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                component={Link as any}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                to={c.to as any}
                sx={{ height: "100%" }}
              >
                <CardContent>
                  <Stack direction="row" alignItems="center" gap={1} mb={1}>
                    <Icon color="primary" fontSize="small" />
                    <Typography variant="subtitle1" fontWeight={600}>
                      {c.title}
                    </Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {c.desc}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          );
        })}
      </Box>
    </Box>
  );
}
